/** @format */

import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import UpdateIcon from "@mui/icons-material/Update";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Icon } from "@iconify/react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useMemo } from "react";
import ReviewsIcon from "@mui/icons-material/Reviews";
import JoinFullIcon from "@mui/icons-material/JoinFull";
import { RoleNames } from "../../utility/types/rolesType";
import { useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SourceIcon from "@mui/icons-material/Source";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import JoinInnerIcon from "@mui/icons-material/JoinInner";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import AddCardIcon from "@mui/icons-material/AddCard";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import HowToRegIcon from "@mui/icons-material/HowToReg";

const UseNavConfig = () => {
	const userData = useSelector(selectCurrentUser);
	const location = useLocation();
	const isAdminRoute = location.pathname.startsWith("/admin");
	const hasCircleLeaderRole = userData?.roles?.some(
		(r) => r.roleName === RoleNames.UNION_LEADER,
	);
	const hasAdminRole = userData?.roles?.some(
		(r) =>
			r.roleName === RoleNames.ADMIN || r.roleName === RoleNames.SUPER_ADMIN,
	);

	const navConfig = useMemo(() => {
		let config = [
			{
				title: "How It Works",
				path: `/user/${userData?._id}`,
				icon: <HelpOutlineIcon />,
				group: "Account",
			},
			{
				title: "Dashboard",
				path: `/user/${userData?._id}`,
				icon: <AccountCircleIcon />,
				group: "Account",
			},

			{
				title: "Update Account",
				path: `/user/update/${userData?._id}`,
				icon: <UpdateIcon />,
				group: "Account",
			},
			{
				title: "Reset Password",
				path: `/user/password-reset`,
				icon: <LockResetIcon />,
				group: "Account",
			},
		];

		if (hasAdminRole) {
			config.push({
				title: "Admin Dashboard",
				path: `/admin/dashboard`,
				icon: <DashboardIcon />,
				group: "Admin",
			});

			config.push({
				title: "Circle Leader Applications",
				path: `/admin/circle-leader/applications`,
				icon: <SourceIcon />,
				group: "Admin",
			});

			config.push({
				title: "All Users",
				path: `/admin/view-all/users`,
				icon: <PeopleAltIcon />,
				group: "Admin",
			});

			config.push({
				title: "Users Auto-Draft",
				path: `/admin/view-all/users/auto-draft`,
				icon: <CreditScoreIcon />,
				group: "Admin",
			});

			config.push({
				title: "Auto-Draft Reports",
				path: `/admin/view-all/auto-draft/reports`,
				icon: <SummarizeIcon />,
				group: "Admin",
			});

			config.push({
				title: "Contribution Reports",
				path: `/admin/view-all/contribution/reports`,
				icon: <SummarizeIcon />,
				group: "Admin",
			});

			config.push({
				title: "PayOut Reports",
				path: `/admin/view-all/payout/reports`,
				icon: <SummarizeIcon />,
				group: "Admin",
			});

			config.push({
				title: "Revenue Reports",
				path: `/admin/view-all/revenue/reports`,
				icon: <SummarizeIcon />,
				group: "Admin",
			});

			config.push({
				title: "All Circles",
				path: `/admin/view-all/circles`,
				icon: <WorkspacesIcon />,
				group: "Admin",
			});

			config.push({
				title: "All Join Requests",
				path: `/admin/view-all/join-requests`,
				icon: <JoinInnerIcon />,
				group: "Admin",
			});

			config.push({
				title: "All Add Requests",
				path: `/admin/view-all/add-requests`,
				icon: <AddReactionIcon />,
				group: "Admin",
			});

			config.push({
				title: "All Invitations",
				path: "/admin/view-all/invitations",
				icon: <InsertInvitationIcon />,
				group: "Admin",
			});

			// Admins review member-sent invitations across every circle
			config.push({
				title: "Invitation Approvals",
				path: "/invitation-approvals",
				icon: <HowToRegIcon />,
				group: "Admin",
			});
		}

		if (!hasCircleLeaderRole) {
			config.push({
				title: "Be a Circle Leader",
				path: "/applications/leader",
				icon: <Icon icon="fluent-mdl2:party-leader" width={21} />,
				group: "Circles",
			});
		}

		if (userData) {
			config.push({
				title: "View & Join Circles",
				path: "/circles",
				icon: <GroupWorkIcon />,
				group: "Circles",
			});
		}

		if (userData) {
			config.push({
				title: "Your Invites",
				path: "/view-invitation",
				icon: <InsertInvitationIcon />,
				group: "Circles",
			});
		}

		if (
			userData?.isVerified &&
			userData?.unionLeaderOf &&
			userData.unionLeaderOf.length > 0
		) {
			config.push(
				{
					title: "Leadership Circles",
					path: "/my-circles",
					icon: <Diversity1Icon />,
					group: "Circles",
				},
				{
					title: "In-coming Join Requests",
					path: "/join-requests",
					icon: <JoinFullIcon />,
					group: "Circles",
				},
			);
		}

		// Leaders review member-sent invitations for circles they lead
		// (admins already have this link in the Admin group)
		if (
			!hasAdminRole &&
			userData?.isVerified &&
			userData?.unionLeaderOf &&
			userData.unionLeaderOf.length > 0
		) {
			config.push({
				title: "Invitation Approvals",
				path: "/invitation-approvals",
				icon: <HowToRegIcon />,
				group: "Circles",
			});
		}

		if (userData?._id && userData?.isVerified) {
			config.push(
				{
					title: "In-coming Request",
					path: "/add-request",
					icon: <AddCircleIcon />,
					group: "Circles",
				},

				{
					title: "Update Payment Method",
					path: "/update-payment-method",
					icon: <AddCardIcon />,
					group: "Account",
				},
				// {
				//   title: "Cancel Membership",
				//   path: "/cancel-membership",
				//   icon: <GppBadIcon />,
				// },
				{
					title: "Leave A Review",
					path: "/review",
					icon: <ReviewsIcon />,
					group: "Account",
				},
			);
		}

		return config;
	}, [userData, hasAdminRole, hasCircleLeaderRole]); // Depend on userData to recalculate when userData changes

	return navConfig;
};

export default UseNavConfig;
